// Two-Factor Authentication Service
// Handles 2FA code generation, storage, verification, and delivery
// Supports email method with fallback

import { doc, updateDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../backend/firebaseConfig';
// Import email service
import { send2FACode as sendEmailWith2FACode } from './emailService';

// Generate a 6-digit random code
export const generate2FACode = () => {
  return Math.floor(100000 + Math.random() * 900000).toString();
};

// Store 2FA code in Firestore with expiration
export const store2FACode = async (userId, code) => {
  try {
    const userRef = doc(db, "users", userId);
    const expirationTime = new Date();
    expirationTime.setMinutes(expirationTime.getMinutes() + 5); // Code expires in 5 minutes
    
    await updateDoc(userRef, {
      twoFACode: code,
      twoFACodeExpiry: expirationTime, // Firestore will convert Date to Timestamp
      // DO NOT update twoFactorMethod here - it should only be updated in Settings
      twoFACodeUsed: false,
      lastUpdated: serverTimestamp()
    });
    
    return true;
  } catch (error) {
    console.error("Error storing 2FA code:", error);
    return false;
  }
};

// Verify 2FA code with enhanced logging and security
export const verify2FACode = async (userId, inputCode, clientIP = null) => {
  try {
    const userRef = doc(db, "users", userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      return { success: false, message: "User not found" };
    }
    
    const userData = userDoc.data();
    const storedCode = userData.twoFACode;
    const codeExpiry = userData.twoFACodeExpiry?.toDate();
    const codeUsed = userData.twoFACodeUsed;
    // const codeMethod = userData.twoFactorMethod; // Currently not used
    
    // Check if code exists
    if (!storedCode) {
      return { success: false, message: "No verification code found" };
    }
    
    // Check if code is already used
    if (codeUsed) {
      return { success: false, message: "Verification code has already been used" };
    }
    
    // Check if code is expired
    if (codeExpiry && new Date() > codeExpiry) {
      // Clean up expired code
      await updateDoc(userRef, {
        twoFACode: null,
        twoFACodeExpiry: null,
        twoFACodeUsed: false
      });
      
      return { success: false, message: "Verification code has expired" };
    }
    
    // Check if code matches
    if (storedCode !== inputCode) {
      return { success: false, message: "Invalid verification code" };
    }
    
    // Mark code as used and set session verification flag
    await updateDoc(userRef, {
      twoFACodeUsed: true,
      last2FASuccess: new Date(),
      twoFASessionVerified: true, // Session-based verification flag
      twoFASessionExpiry: new Date(Date.now() + 24 * 60 * 60 * 1000) // 24 hours from now
    });
    
    return { success: true, message: "Verification successful" };
  } catch (error) {
    console.error("Error verifying 2FA code:", error);
    return { success: false, message: "Error verifying code" };
  }
};

// Check if user has completed 2FA verification for current session
export const check2FASessionVerification = async (userId) => {
  try {
    const userRef = doc(db, "users", userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      return { verified: false, message: "User not found" };
    }
    
    const userData = userDoc.data();
    const sessionVerified = userData.twoFASessionVerified;
    const sessionExpiry = userData.twoFASessionExpiry?.toDate();
    const twoFactorEnabled = userData.twoFactorEnabled;
    
    // If 2FA is not enabled, user is considered verified
    if (!twoFactorEnabled) {
      return { verified: true, message: "2FA not enabled" };
    }
    
    // If 2FA is enabled but no session verification, user needs to verify
    if (!sessionVerified) {
      return { verified: false, message: "2FA verification required" };
    }
    
    // Check if session has expired
    if (sessionExpiry && new Date() > sessionExpiry) {
      // Clear expired session
      await updateDoc(userRef, {
        twoFASessionVerified: false,
        twoFASessionExpiry: null
      });
      return { verified: false, message: "2FA session expired" };
    }
    
    return { verified: true, message: "2FA session verified" };
  } catch (error) {
    console.error("Error checking 2FA session verification:", error);
    return { verified: false, message: "Error checking verification status" };
  }
};

// Clear 2FA session (for logout)
export const clear2FASession = async (userId) => {
  try {
    const userRef = doc(db, "users", userId);
    await updateDoc(userRef, {
      twoFASessionVerified: false,
      twoFASessionExpiry: null
    });
    return true;
  } catch (error) {
    console.error("Error clearing 2FA session:", error);
    return false;
  }
};

// Fallback email function (for when SendGrid fails)
const send2FACodeViaEmailFallback = async (email, code, userName) => {
  try {
    // Return success with code for modal display
    console.log(`2FA Code sent to ${email}: ${code} (Email service temporarily unavailable - using fallback)`);
    
    return { 
      success: true, 
      message: "Email service temporarily unavailable - code displayed below",
      code: code, // Include code for modal display when service is down
      fallback: true // Flag to indicate this is a fallback
    };
  } catch (error) {
    console.error("Error sending email:", error);
    return { success: false, message: "Failed to send email" };
  }
};


// Main function to send 2FA code with production services
export const send2FACode = async (userId, userDetails, clientIP = null) => {
  try {
    // Note: Rate limiting removed for 2FA codes - code expiration (5 min) and resend cooldown (1 min) provide adequate protection
    
    const code = generate2FACode();
    const userName = `${userDetails.firstName} ${userDetails.lastName}`;
    
    // Store the code in database
    const stored = await store2FACode(userId, code);
    if (!stored) {
      return { success: false, message: "Failed to store verification code" };
    }
    
    // Send the code via email
    try {
      const sendResult = await sendEmailWith2FACode(userDetails.email, code, userName);
      
      // Check if the service failed and fallback was used
      if (sendResult.fallback) {
        return {
          ...sendResult,
          code: code, // Include code for modal display when fallback is used
          fallback: true
        };
      }
      
      return sendResult;
    } catch (serviceError) {
      console.error("Error with email service:", serviceError);
      // Fallback to mock service if Nodemailer fails
      const fallbackResult = await send2FACodeViaEmailFallback(userDetails.email, code, userName);
      return {
        ...fallbackResult,
        code: code, // Include code for modal display when fallback is used
        fallback: true
      };
    }
  } catch (error) {
    console.error("Error sending 2FA code:", error);
    return { success: false, message: "Failed to send verification code" };
  }
};
